<template>
    <div class="class-management-container container">
      <el-card shadow="hover">
        <template #header>
          <div class="card-header">
            <h2>إدارة الفصول الدراسية</h2>
            <el-button type="primary" @click="handleCreate">
              <el-icon><Plus /></el-icon> إضافة فصل جديد
            </el-button>
          </div>
        </template>
        
        <el-table :data="classes" v-loading="loading" border style="width: 100%">
          <el-table-column prop="name" label="اسم الفصل" />
          <el-table-column prop="grade_level.name" label="المستوى الدراسي" />
          <el-table-column prop="study_year.name" label="السنة الدراسية" />
          <el-table-column label="الحالة" width="100">
            <template #default="{ row }">
              <el-tag :type="row.is_active ? 'success' : 'danger'">
                {{ row.is_active ? 'نشط' : 'غير نشط' }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column label="الإجراءات" width="150" fixed="right">
            <template #default="{ row }">
              <el-button size="small" @click="handleEdit(row)">تعديل</el-button>
              <el-popconfirm title="هل أنت متأكد من حذف هذا الفصل؟" @confirm="handleDelete(row.id)">
                <template #reference><el-button size="small" type="danger">حذف</el-button></template>
              </el-popconfirm>
            </template>
          </el-table-column>
        </el-table>
      </el-card>
  
      <!-- Dialog لإنشاء/تعديل الفصل -->
      <el-dialog v-model="dialogVisible" :title="dialogTitle" width="50%">
        <el-form :model="form" ref="formRef" label-position="top" :rules="rules">
          <el-form-item label="اسم الفصل" prop="name">
            <el-input v-model="form.name" placeholder="مثال: الصف الأول (أ)" />
          </el-form-item>
          <el-form-item label="الوصف" prop="description">
            <el-input v-model="form.description" type="textarea" placeholder="وصف موجز للفصل" />
          </el-form-item>
          <el-row :gutter="20">
            <el-col :span="12">
              <el-form-item label="المستوى الدراسي" prop="grade_level_id">
                <el-select v-model="form.grade_level_id" placeholder="اختر المستوى" style="width: 100%;">
                  <el-option v-for="level in gradeLevels" :key="level.id" :label="level.name" :value="level.id" />
                </el-select>
              </el-form-item>
            </el-col>
            <el-col :span="12">
              <el-form-item label="السنة الدراسية" prop="study_year_id">
                <el-select v-model="form.study_year_id" placeholder="اختر السنة الدراسية" style="width: 100%;">
                  <el-option v-for="year in studyYears" :key="year.id" :label="year.name" :value="year.id" />
                </el-select>
              </el-form-item>
            </el-col>
          </el-row>
          <el-form-item label="حالة الفصل">
            <el-switch v-model="form.is_active" active-text="نشط" inactive-text="غير نشط" />
          </el-form-item>
        </el-form>
        <template #footer>
          <span class="dialog-footer">
            <el-button @click="dialogVisible = false">إلغاء</el-button>
            <el-button type="primary" @click="submitForm" :loading="formLoading">حفظ</el-button>
          </span>
        </template>
      </el-dialog>
    </div>
  </template>
  
  <script>
  import axios from 'axios';
  import { ElMessage, ElPopconfirm } from 'element-plus';
  import { Plus } from '@element-plus/icons-vue';
  
  export default {
    name: 'ClassManagement',
    components: { Plus, ElPopconfirm },
    data() {
      return {
        api: null,
        loading: true,
        classes: [],
        gradeLevels: [],
        studyYears: [],
        dialogVisible: false,
        formLoading: false,
        form: this.getInitialForm(),
        rules: {
          name: [{ required: true, message: 'اسم الفصل مطلوب', trigger: 'blur' }],
          grade_level_id: [{ required: true, message: 'المستوى الدراسي مطلوب', trigger: 'change' }],
          study_year_id: [{ required: true, message: 'السنة الدراسية مطلوبة', trigger: 'change' }],
        },
      };
    },
    computed: {
      dialogTitle() {
        return this.form.id ? 'تعديل فصل دراسي' : 'إضافة فصل دراسي جديد';
      },
    },
    created() {
      this.api = axios.create({ baseURL: process.env.VUE_APP_API_BASE_URL || 'https://cale.caledonian.ly/api' });
      this.api.interceptors.request.use((config) => {
        const token = localStorage.getItem('access_token');
        if (token) config.headers['Authorization'] = `Bearer ${token}`;
        return config;
      });
      this.fetchClasses();
      this.fetchFormData();
    },
    methods: {
      getInitialForm() {
        return {
          id: null,
          name: '',
          description: '',
          grade_level_id: null,
          study_year_id: null,
          is_active: true,
        };
      },
      async fetchClasses() {
        this.loading = true;
        try {
          const response = await this.api.get('/classes');
          this.classes = response.data;
        } catch (error) {
          ElMessage.error('فشل في جلب الفصول الدراسية.');
        } finally {
          this.loading = false;
        }
      },
      async fetchFormData() {
        try {
          const response = await this.api.get('/classes/form-data');
          this.gradeLevels = response.data.grade_levels;
          this.studyYears = response.data.study_years;
        } catch (error) {
          ElMessage.error('فشل في جلب بيانات الفورم.');
        }
      },
      handleCreate() {
        this.form = this.getInitialForm();
        this.dialogVisible = true;
      },
      handleEdit(classData) {
        this.form = { ...classData }; // Create a copy to avoid reactive issues
        this.dialogVisible = true;
      },
      async submitForm() {
        if (!this.$refs.formRef) return;
        await this.$refs.formRef.validate(async (valid) => {
          if (!valid) return;
          this.formLoading = true;
          
          try {
            if (this.form.id) {
              await this.api.put(`/classes/${this.form.id}`, this.form);
              ElMessage.success('تم تحديث الفصل بنجاح!');
            } else {
              await this.api.post('/classes', this.form);
              ElMessage.success('تم إنشاء الفصل بنجاح!');
            }
            this.dialogVisible = false;
            this.fetchClasses();
          } catch (error) {
            const errorMsg = error.response?.data?.errors ? Object.values(error.response.data.errors)[0][0] : (error.response?.data?.message || 'فشلت العملية.');
            ElMessage.error(errorMsg);
          } finally {
            this.formLoading = false;
          }
        });
      },
      async handleDelete(id) {
        try {
          await this.api.delete(`/classes/${id}`);
          ElMessage.success('تم حذف الفصل بنجاح!');
          this.fetchClasses();
        } catch (error) {
          ElMessage.error(error.response?.data?.message || 'فشل في حذف الفصل.');
        }
      },
    },
  };
  </script>
  
  <style scoped>
  .class-management-container { padding: 20px; }
  .card-header { display: flex; justify-content: space-between; align-items: center; }
  .form-tip {
    color: #909399;
    font-size: 12px;
    margin-bottom: 20px;
  }
  .el-form-item {
    margin-bottom: 22px;
  }
  </style>
  